import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Backdrop, Tabs, Tab } from '@material-ui/core';
import NoSleep from 'nosleep.js';
import Layout from '../components/Layout';
import String from '../components/String';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Map from '../components/Map';
import { useLanguage } from '../contexts/language';
import { useExperience } from '../contexts/experience';
import useVisibility from '../hooks/useVisibility';
import ManualCode from '../components/ManualCode';
import Replay from '../components/Replay';
import PlayerOnMap from '../components/PlayerOnMap';
import { useSession } from '../contexts/session';
import VideoPlayerOnMap from '../components/VideoPlayerOnMap';
import { CreditsOverlay } from '../components/CreditsOverlay';
import { useBackButton } from '../hooks/useBackButton';

const noSleep = new NoSleep();

export default function Experience() {
  const { alarmSound, content } = useLanguage();
  const isVisible = useVisibility();
  const { start, started, isActive, hasManualSpots, hasGeoSpots, canReplay, mapSpots, isVideoActive } =
    useExperience();
  const { selectedExperience } = useSession();

  const backgroundRef = useRef(null);
  const shouldHideBackground = !started;

  const backButtonReset = React.useCallback(() => {
    window.location = '/';
  }, []);
  useBackButton(backButtonReset);

  const showTabs = React.useMemo(
    () =>
      (hasManualSpots && hasGeoSpots) ||
      (hasManualSpots && selectedExperience.creditsEnabled) ||
      (hasGeoSpots && selectedExperience.creditsEnabled),
    [hasManualSpots, hasGeoSpots, selectedExperience.creditsEnabled]
  );

  const [tab, setTab] = useState(!hasGeoSpots && hasManualSpots ? 'code' : 'map');

  const handleStart = () => {
    noSleep.enable();
    start();
  };

  useEffect(() => {
    // prevent pull-down-to-refresh
    document.body.classList.add('no-pull-refresh');

    return () => {
      document.body.classList.remove('no-pull-refresh');
      noSleep.disable();
    };
  }, []);

  useEffect(() => {
    const node = backgroundRef.current;
    if (!node) return;
    if (shouldHideBackground) {
      node.setAttribute('inert', '');
    } else {
      node.removeAttribute('inert');
    }
  }, [shouldHideBackground]);

  return (
    <Layout
      fullscreen
      top={<Header />}
      bottom={
        <Footer>
          <Typography variant="caption" color="textSecondary">
            {content('footer_experience_prefix')}
            <a
              href="https://mezzoforte.design/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit' }}
            >
              {content('footer_experience_link')}
            </a>
          </Typography>
        </Footer>
      }
    >
      {showTabs && (
        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          aria-label="tabs"
          style={{ width: '100%', display: 'flex' }}
        >
          {hasGeoSpots && (
            <Tab
              label={content('map')}
              value="map"
              style={{ flex: 1, maxWidth: 'unset' }}
              disabled={!started || isActive || isVideoActive}
            />
          )}
          {hasManualSpots && (
            <Tab
              label={content('code')}
              value="code"
              style={{ flex: 1, maxWidth: 'unset' }}
              disabled={!started || isActive || isVideoActive}
            />
          )}
          {selectedExperience.creditsEnabled && (
            <Tab
              label={content('credits')}
              value="credits"
              style={{ flex: 1, maxWidth: 'unset' }}
              disabled={!started || isActive || isVideoActive}
            />
          )}
        </Tabs>
      )}
      <Layout fullscreen>
        <Box ref={backgroundRef} aria-hidden={shouldHideBackground} style={{ width: '100%', height: '100%' }}>
          {started && (
            <>
              {hasGeoSpots && <Map spots={mapSpots} userLocation />}
              {hasManualSpots && <ManualCodeOverlay open={tab === 'code'} />}
              {selectedExperience.creditsEnabled && <CreditsOverlay open={tab === 'credits'} />}
              <PlayerOnMap />
              <VideoPlayerOnMap />
              <ReplayOverlay open={canReplay} />
            </>
          )}
        </Box>
        <StartOverlay open={!started} onStart={handleStart} />
      </Layout>

      {started && !isVisible && hasGeoSpots && (
        <audio src={alarmSound} autoPlay loop style={{ display: 'none' }} aria-hidden="true" />
      )}
    </Layout>
  );
}

function ManualCodeOverlay({ open }) {
  const { content } = useLanguage();
  return (
    <Backdrop
      style={{ position: 'absolute', zIndex: 5 }}
      open={open}
      aria-hidden={!open}
      role="dialog"
      aria-label={content('aria_manual_playback_code_dialog')}
    >
      <Layout justify="center">
        <ManualCode />
      </Layout>
    </Backdrop>
  );
}

function ReplayOverlay({ open }) {
  const { content } = useLanguage();
  return (
    <Backdrop
      style={{ position: 'absolute', zIndex: 10 }}
      open={open}
      aria-hidden={!open}
      role="dialog"
      aria-label={content('aria_replay_content_dialog')}
      aria-modal="true"
    >
      <Layout justify="center">{open && <Replay />}</Layout>
    </Backdrop>
  );
}

function StartOverlay({ open, onStart }) {
  const { selectedExperience } = useSession();
  const startButtonRef = useRef();
  const { content } = useLanguage();

  useEffect(() => {
    if (open && startButtonRef.current) {
      startButtonRef.current.focus();
    }
  }, [open]);

  return (
    <Backdrop
      style={{ position: 'absolute', zIndex: 10 }}
      open={open}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-overlay-title"
      aria-describedby="start-overlay-description"
    >
      <Layout justify="center">
        <Box textAlign="center" ml={4} mr={4}>
          <Typography id="start-overlay-title" variant="title" mb={2}>
            {selectedExperience?.welcome_message ?? <String s="areyouready" />}
          </Typography>
          <Typography id="start-overlay-description" variant="text" mb={3} aria-live="polite">
            {selectedExperience?.description ?? <String s="beforestart" />}
          </Typography>
          <Button
            ref={startButtonRef}
            color="primary"
            variant="contained"
            onClick={onStart}
            data-testid="ready-start-btn"
            aria-label={content('start')}
          >
            <String s="start" />
          </Button>
        </Box>
      </Layout>
    </Backdrop>
  );
}
